import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useAnalyses } from "@/context/AnalysisContext";
import { EmptyState } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, TrendingUp, Zap, Activity, Award, AlertCircle, Sparkles } from "lucide-react";
import { tierFor, formatDate } from "@/lib/utils-app";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  LineChart, Line, AreaChart, Area, LabelList,
} from "recharts";
import { useNavigate } from "react-router-dom";

const CAT_LABELS = {
  hook_score: "Hook",
  visual_quality: "Visual",
  copy_score: "Copy",
  cta_score: "CTA",
  emotional_score: "Emotion",
  product_visibility: "Brand",
  retention_score: "Retention",
  pacing_score: "Pacing",
  audio_design: "Audio",
  audience_fit: "Audience",
  trend_alignment: "Trend",
  brand_recall: "Recall",
  scroll_stop: "Stop",
};

// Animated tabular counter
function Counter({ value, duration = 1.1, suffix = "" }) {
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    let raf; const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span className="tabular">{n}{suffix}</span>;
}

function StatTile({ icon: Icon, label, value, accent = "primary", delay = 0, suffix = "" }) {
  const color = {
    primary: "hsl(var(--primary))",
    sky: "#38BDF8",
    emerald: "#10B981",
    amber: "#F59E0B",
    rose: "#F43F5E",
    violet: "#A78BFA",
  }[accent];
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      <Card className="relative p-5 overflow-hidden ring-accent">
        <div className="absolute -top-10 -right-10 h-24 w-24 rounded-full blur-2xl opacity-40" style={{ background: color }} />
        <div className="relative flex items-center gap-2 text-muted-foreground mb-3">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: `${color}22`, color }}>
            <Icon className="h-3.5 w-3.5" />
          </span>
          <span className="overline">{label}</span>
        </div>
        <div className="relative font-display text-3xl font-bold tabular tracking-tight" style={{ color }}>
          <Counter value={value} suffix={suffix} />
        </div>
      </Card>
    </motion.div>
  );
}

export default function Analytics() {
  const { analyses } = useAnalyses();
  const navigate = useNavigate();

  const categoryAvgs = useMemo(() => {
    const totals = {}; const counts = {};
    analyses.forEach((a) => {
      (a.ai_analysis?.categories || []).forEach((c) => {
        totals[c.key] = (totals[c.key] || 0) + c.score;
        counts[c.key] = (counts[c.key] || 0) + 1;
      });
    });
    return Object.keys(CAT_LABELS)
      .filter((k) => counts[k])
      .map((k) => ({ key: k, name: CAT_LABELS[k], score: Math.round(totals[k] / counts[k]) }));
  }, [analyses]);

  const trend = useMemo(() => {
    return [...analyses]
      .reverse()
      .map((a, i) => ({
        idx: i + 1,
        score: a.overall_score || 0,
        label: (a.metadata?.title || "").slice(0, 24),
        date: formatDate(a.created_at),
      }));
  }, [analyses]);

  const distribution = useMemo(() => {
    const buckets = [
      { name: "0-20", min: 0, max: 20, count: 0 },
      { name: "21-40", min: 21, max: 40, count: 0 },
      { name: "41-60", min: 41, max: 60, count: 0 },
      { name: "61-75", min: 61, max: 75, count: 0 },
      { name: "76-90", min: 76, max: 90, count: 0 },
      { name: "91-100", min: 91, max: 100, count: 0 },
    ];
    analyses.forEach((a) => {
      const s = a.overall_score || 0;
      const b = buckets.find((x) => s >= x.min && s <= x.max);
      if (b) b.count += 1;
    });
    return buckets;
  }, [analyses]);

  const topAnalyses = useMemo(() => {
    return [...analyses].sort((a, b) => (b.overall_score || 0) - (a.overall_score || 0)).slice(0, 5);
  }, [analyses]);

  if (analyses.length === 0) {
    return (
      <div className="space-y-6">
        <Header />
        <EmptyState
          icon={BarChart3}
          title="No data yet"
          description="Analyze a YouTube video to see performance analytics across 13 creative dimensions."
        />
      </div>
    );
  }

  const avg = Math.round(analyses.reduce((s, a) => s + (a.overall_score || 0), 0) / analyses.length);
  const best = Math.max(...analyses.map((a) => a.overall_score || 0));
  const worst = Math.min(...analyses.map((a) => a.overall_score || 0));
  const strongest = categoryAvgs.length ? categoryAvgs.reduce((a, b) => (b.score > a.score ? b : a)) : null;
  const weakest = categoryAvgs.length ? categoryAvgs.reduce((a, b) => (b.score < a.score ? b : a)) : null;

  const tooltipStyle = {
    background: "hsl(var(--popover))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 12,
    color: "hsl(var(--foreground))",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <Header count={analyses.length} />

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatTile icon={Activity} label="Analyses" value={analyses.length} accent="sky" delay={0} />
        <StatTile icon={Zap} label="Avg Score" value={avg} accent="primary" delay={0.06} />
        <StatTile icon={Award} label="Top Score" value={best} accent="emerald" delay={0.12} />
        <StatTile icon={AlertCircle} label="Low Score" value={worst} accent="amber" delay={0.18} />
      </div>

      {/* Horizontal bar chart (replaces old radar) + Trend */}
      <div className="grid lg:grid-cols-5 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}
          className="lg:col-span-3"
        >
          <Card className="relative p-6 overflow-hidden">
            <div className="absolute inset-0 grid-bg-sm radial-fade opacity-20 pointer-events-none" />
            <div className="relative flex items-baseline justify-between mb-2">
              <div>
                <p className="overline text-muted-foreground">Creative Fingerprint</p>
                <h2 className="font-display text-lg font-semibold tracking-tight mt-1">Avg score by dimension</h2>
              </div>
              <Badge className="border-0 bg-primary/10 text-primary">13 metrics</Badge>
            </div>
            <div className="relative h-[420px]" data-testid="dimension-bars">
              <ResponsiveContainer width="100%" height="100%" minHeight={400}>
                <BarChart
                  data={categoryAvgs}
                  layout="vertical"
                  margin={{ top: 4, right: 32, left: 10, bottom: 4 }}
                  barCategoryGap={6}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={tooltipStyle} />
                  <Bar dataKey="score" radius={[0, 6, 6, 0]} isAnimationActive>
                    {categoryAvgs.map((d, i) => (
                      <Cell key={i} fill={tierFor(d.score).color} />
                    ))}
                    <LabelList dataKey="score" position="right" style={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.26, duration: 0.5 }}
          className="lg:col-span-2"
        >
          <Card className="relative p-6 overflow-hidden h-full">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <p className="overline text-muted-foreground">Trend</p>
                <h2 className="font-display text-lg font-semibold tracking-tight mt-1">Score Over Time</h2>
              </div>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </div>
            <div className="h-64" data-testid="trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                  <defs>
                    <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="idx" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={(l) => `Analysis #${l}`} />
                  <Area type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#scoreGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
              <div className="rounded-lg border border-border p-2">
                <p className="overline text-muted-foreground">Strongest</p>
                <p className="font-medium mt-0.5">{strongest?.name || "—"} · <span className="tabular text-emerald-500">{strongest?.score ?? "—"}</span></p>
              </div>
              <div className="rounded-lg border border-border p-2">
                <p className="overline text-muted-foreground">Weakest</p>
                <p className="font-medium mt-0.5">{weakest?.name || "—"} · <span className="tabular text-rose-500">{weakest?.score ?? "—"}</span></p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Distribution */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38, duration: 0.5 }}
      >
        <Card className="p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <p className="overline text-muted-foreground">Score buckets</p>
              <h2 className="font-display text-lg font-semibold tracking-tight mt-1">Distribution</h2>
            </div>
            <span className="text-xs text-muted-foreground tabular">n={analyses.length}</span>
          </div>
          <div className="h-64" data-testid="distribution-chart">
            <ResponsiveContainer width="100%" height="100%" minHeight={240}>
              <BarChart data={distribution} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={tooltipStyle} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {distribution.map((d, i) => (
                    <Cell key={i} fill={tierFor(d.max).color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </motion.div>

      {/* Leaderboard */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.44, duration: 0.5 }}>
        <Card className="p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <p className="overline text-muted-foreground">Top performers</p>
              <h2 className="font-display text-lg font-semibold tracking-tight mt-1">Highest scoring videos</h2>
            </div>
          </div>
          <div className="divide-y divide-border" data-testid="leaderboard">
            {topAnalyses.map((a, i) => {
              const t = tierFor(a.overall_score || 0);
              return (
                <button
                  key={a.id}
                  onClick={() => navigate(`/analysis/${a.id}`)}
                  className="w-full flex items-center gap-3 py-3 hover:bg-secondary/40 rounded-lg px-2 -mx-2 transition-colors text-left"
                >
                  <span className="font-display text-xl font-black w-6 text-muted-foreground tabular">{i + 1}</span>
                  <img src={a.metadata?.thumbnail} alt="" className="h-10 w-16 object-cover rounded-md" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{a.metadata?.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{a.metadata?.channel}</p>
                  </div>
                  <span
                    className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold tabular"
                    style={{ backgroundColor: t.color, color: "#fff" }}
                  >
                    {a.overall_score}
                  </span>
                </button>
              );
            })}
          </div>
        </Card>
      </motion.div>
    </div>
  );
}

function Header({ count }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-3">
      <div>
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Sparkles className="h-3.5 w-3.5" />
          </span>
          <p className="overline text-muted-foreground">Performance intelligence</p>
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">
          Analytics {count != null && <span className="text-muted-foreground tabular text-xl">/ {count}</span>}
        </h1>
      </div>
    </div>
  );
}
